import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaPlus, FaBroadcastTower } from 'react-icons/fa';

import config from '../../config.js';
import { useFetchWrapper } from '../../_helpers/fetchWrapper.js';
import EditEventModal from './modal/EditEventModal.jsx';
import ConfirmationModal from './modal/ConfirmationModal.jsx';
import { serverMessage } from '../../_helpers/errorMessage.js';
import ActionMenuButton from './ActionMenuButton.jsx';
import styles from '../../styles/partial/PerformerEvents.module.css';
import { useReload } from '../../contexts/ReloadContext.jsx';
import { useLocale } from '../../contexts/LocaleContext.jsx';

const PerformerEvent = ({ reloadKey }) => {
  const { bandid } = useParams();
  const navigate = useNavigate();
  const fetchWrapper = useFetchWrapper();
  const { t, format } = useLocale();

  const [futureEvents, setFutureEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [eventsFetched, setEventsFetched] = useState(false);
  const [isAddEventModalOpen, setIsAddEventModalOpen] = useState(false);
  // A failed load is shown as such, with Retry, not as "no events" (ENG-510).
  const [loadFailed, setLoadFailed] = useState(false);
  // The event awaiting a Delete confirmation, and why the last delete failed.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { reload } = useReload();

  const lastReloadKeyRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    try {
      const events = await fetchWrapper.get(`${config.apiBaseUrl}/bands/${bandid}/shows`);

      const future = (events?.futureEvents && events.futureEvents.length > 0) ? events.futureEvents : [];
      const past = (events?.pastEvents && events.pastEvents.length > 0) ? events.pastEvents : [];

      setFutureEvents(future);
      setPastEvents(past);
      setLoadFailed(false);
    } catch (error) {
      console.error('Error loading events:', error);
      setLoadFailed(true);
    } finally {
      setEventsFetched(true);
    }
  }, [bandid, fetchWrapper]);

  const retryLoad = () => {
    setLoadFailed(false);
    setEventsFetched(false);
  };

  useEffect(() => {
    if (lastReloadKeyRef.current !== reloadKey) {
      setEventsFetched(false);
      lastReloadKeyRef.current = reloadKey;
    }

    if (eventsFetched) return;
    fetchEvents();
  }, [eventsFetched, fetchEvents, reloadKey]);

  const handleEventEdit = (event) => {
    setCurrentEvent(event);
    setEditModalOpen(true);
  };

  // Delete asks first (ENG-510); it used to remove the event on the first click.
  const handleFutureEventDelete = (event) => {
    setDeleteError(null);
    setPendingDelete(event);
  };

  const confirmDelete = async () => {
    if (!pendingDelete || isDeleting) return;
    const eventId = pendingDelete.id;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await fetchWrapper.delete(`${config.apiBaseUrl}/bands/${bandid}/shows/${eventId}`);
      setFutureEvents((prevEvents) => prevEvents.filter((event) => event.id !== eventId));
      setPendingDelete(null);
    } catch (error) {
      console.error('Error deleting event:', error);
      setDeleteError(serverMessage(error) || t('events.deleteModal.failed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMenuToggle = (eventId) => {
    if (openMenuId === eventId) {
      setOpenMenuId(null);
      setHighlightedRow(null);
    } else {
      setOpenMenuId(eventId);
      setHighlightedRow(eventId);
    }
  };

  const openAddEventModal = () => setIsAddEventModalOpen(true);
  const closeAddEventModal = () => setIsAddEventModalOpen(false);

  // The band menu's Add Event fires a window event (ENG-510). Only
  // PerformerNavEvents listened for it, and it isn't mounted on this page,
  // so the menu item did nothing.
  useEffect(() => {
    const onAddEvent = () => setIsAddEventModalOpen(true);
    window.addEventListener('addEventTriggered', onAddEvent);
    return () => window.removeEventListener('addEventTriggered', onAddEvent);
  }, []);

  // A failed save rejects, so EditEventModal stays open and shows why.
  const handleSaveNewEvent = async (eventData) => {
    await fetchWrapper.post(`${config.apiBaseUrl}/bands/${bandid}/shows`, eventData);
    reload();
    closeAddEventModal();
  };

  const handleGoLive = () => {
    navigate(`/performers/${bandid}/stream`);
  };

  const closeModal = () => {
    setEditModalOpen(false);
    setCurrentEvent(null);
  };

  const handleEventSave = async (updatedEvent) => {
    updatedEvent.dateTime = new Date(updatedEvent.dateTime).toISOString();
    updatedEvent.cost = parseFloat(updatedEvent.cost);

    await fetchWrapper.put(`${config.apiBaseUrl}/bands/${bandid}/shows/${updatedEvent.id}`, updatedEvent);
    setFutureEvents((prevEvents) =>
      prevEvents.map((event) =>
        event.id === updatedEvent.id ? updatedEvent : event
      )
    );
    closeModal();
    reload();
  };

  // Every cell in a row, including the action cell, shares one background so the
  // row reads as a single bar and highlights as a whole while its menu is open
  const getCellClass = (eventId, columnClass) => {
    const cellClass = highlightedRow === eventId ? styles.gridCellHighlight : styles.gridCell;
    return columnClass ? `${cellClass} ${columnClass}` : cellClass;
  };

  return (
    <div className={styles.page}>
      <div className={styles.actionBar}>
        <button className={styles.btnPrimary} onClick={openAddEventModal}>
          <FaPlus aria-hidden="true" /> {t('events.actions.addEvent')}
        </button>
        <button className={styles.btnSecondary} onClick={handleGoLive}>
          <FaBroadcastTower aria-hidden="true" /> {t('events.actions.goLive')}
        </button>
      </div>

      <EditEventModal
        bandEvent={null}
        isOpen={isAddEventModalOpen}
        onClose={closeAddEventModal}
        onSave={handleSaveNewEvent}
      />

      <div className={styles.eventsContainer}>
        {loadFailed ? (
          <div className={styles.loadError} role="alert">
            <p>{t('events.loadFailed')}</p>
            <button type="button" className={styles.btnSecondary} onClick={retryLoad}>
              {t('events.retry')}
            </button>
          </div>
        ) : (
        <>
        {futureEvents.length > 0 && (
          <section className={styles.section} aria-label={t('events.upcoming.heading')}>
            <h2 className={styles.sectionHeading}>{t('events.upcoming.heading')}</h2>
            <div className={styles.grid} role="table" aria-label={t('events.upcoming.tableLabel')}>
              <div role="rowgroup">
                <div role="row">
                  <div className={styles.gridHeader} role="columnheader">{t('events.columns.date')}</div>
                  <div className={styles.gridHeader} role="columnheader">{t('events.columns.title')}</div>
                  <div className={`${styles.gridHeader} ${styles.locationHeader}`} role="columnheader">{t('events.columns.location')}</div>
                  <div className={`${styles.gridHeader} ${styles.actionHeader}`} role="columnheader">{t('events.columns.action')}</div>
                </div>
              </div>
              <div role="rowgroup">
                {futureEvents.map(event => (
                  <div key={event.id} role="row">
                    <div className={getCellClass(event.id)} role="cell">{format.dateTime(event.dateTime, 'eventListing')}</div>
                    <div className={getCellClass(event.id, styles.titleCell)} role="cell">{event.title}</div>
                    <div className={getCellClass(event.id, styles.locationCell)} role="cell">{event.location}</div>
                    <div className={getCellClass(event.id, styles.actionCell)} role="cell">
                      <ActionMenuButton
                        itemId={event.id}
                        isOpen={openMenuId === event.id}
                        onToggle={handleMenuToggle}
                        triggerLabel={t('events.actions.menu')}
                        menuItems={[
                          { label: t('common.actions.edit'), icon: FaEdit, action: () => handleEventEdit(event) },
                          { label: t('common.actions.delete'), icon: FaTrash, action: () => handleFutureEventDelete(event) },
                        ]}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className={styles.section} aria-label={t('events.past.heading')}>
          <h2 className={styles.sectionHeading}>{t('events.past.heading')}</h2>
          {pastEvents.length === 0 ? (
            <p className={styles.emptyState}>{t('events.past.empty')}</p>
          ) : (
            <div className={styles.grid} role="table" aria-label={t('events.past.tableLabel')}>
              <div role="rowgroup">
                <div role="row">
                  <div className={styles.gridHeader} role="columnheader">{t('events.columns.date')}</div>
                  <div className={styles.gridHeader} role="columnheader">{t('events.columns.title')}</div>
                  <div className={`${styles.gridHeader} ${styles.locationHeader}`} role="columnheader">{t('events.columns.location')}</div>
                  <div className={`${styles.gridHeader} ${styles.actionHeader}`} role="columnheader">{t('events.columns.action')}</div>
                </div>
              </div>
              <div role="rowgroup">
                {pastEvents.map(event => (
                  <div key={event.id} role="row">
                    <div className={styles.gridCell} role="cell">{format.dateTime(event.dateTime, 'eventListing')}</div>
                    <div className={`${styles.gridCell} ${styles.titleCell}`} role="cell">{event.title}</div>
                    <div className={`${styles.gridCell} ${styles.locationCell}`} role="cell">{event.location}</div>
                    <div className={`${styles.gridCell} ${styles.actionCell}`} role="cell">
                      {/* The show's post-show digest (ENG-446). */}
                      <Link
                        to={`/performers/${bandid}/shows/${event.id}/report`}
                        className={styles.recapBtn}
                        data-testid={`event-recap-${event.id}`}
                      >
                        {t('events.actions.recap')}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
        </>
        )}
      </div>

      <ConfirmationModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        confirmTestId="event-delete-confirm"
        title={t('events.deleteModal.title')}
        message={pendingDelete ? t('events.deleteModal.message', { title: pendingDelete.title }) : ''}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        isDangerous={true}
        error={deleteError}
        isBusy={isDeleting}
      />

      <EditEventModal
        bandEvent={currentEvent}
        isOpen={editModalOpen}
        onClose={closeModal}
        onSave={handleEventSave}
      />
    </div>
  );
};

export default PerformerEvent;
